<template>
    <div>
        <page-title>{{ title }}</page-title>
        <user-route-tabs :user-id="userId"></user-route-tabs>
        <h3 class="my-5">{{ $t('labels.user_addresses') }}</h3>
        <user-addresses-table
            v-if="isSuperAdmin"
            ref="addressTable"
            :user-id="userId"
            @updated="loadSortList"
        >
            <template v-slot:after>
                <v-btn
                    color="success"
                    class="mt-5"
                    :to="`/users/${userId}/addresses/create?redirectTo=/users/${userId}/addresses`"
                >
                    {{ $t('actions.create') }}
                </v-btn>
            </template>
        </user-addresses-table>

        <h3 class="my-5">{{ $t('labels.company_addresses') }}</h3>
        <overlay :show="busy">
            <v-data-table
                v-if="user && user.company"
                :value="selectedCompanyAddresses"
                :items="companyAddresses"
                :headers="headers"
                class="data-table"
                show-select
                disable-filtering
                disable-pagination
                must-sort
                hide-default-footer
                item-key="id"
                @input="onInput"
            >
            </v-data-table>
        </overlay>

        <div v-if="isSuperAdmin" class="pt-6">
            <div class="d-flex align-center">
                <h3 class="my-5">{{ $t('labels.address_sorting') }}</h3>
                <v-spacer></v-spacer>
                <v-btn
                    color="primary"
                    :disabled="!orderIsChanged"
                    :loading="orderSaveLoading"
                    @click="saveOrder"
                    >{{ $t('actions.save_sorting') }}</v-btn
                >
            </div>
            <draggable
                v-model="sortedAddresses"
                v-bind="dragOptions"
                @end="orderChanged"
            >
                <v-card
                    v-for="address in sortedAddresses"
                    :key="address.id"
                    elevation="0"
                    class="rounded text-body-2"
                    style="margin-bottom: 1px"
                >
                    <v-row class="align-center">
                        <div class="px-6">
                            <v-icon>drag_indicator</v-icon>
                        </div>
                        <v-col cols="4" class="py-2">
                            {{ address.name }}
                        </v-col>
                        <v-col class="flex-grow-1 py-2">
                            {{ address.address }}
                        </v-col>
                        <div class="px-6">
                            <v-tooltip top>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon v-bind="attrs" v-on="on">{{
                                        address.companyId ? 'business' : 'house'
                                    }}</v-icon>
                                </template>
                                <span>{{
                                    $t(
                                        address.companyId
                                            ? 'labels.company_addresses'
                                            : 'labels.addresses'
                                    )
                                }}</span>
                            </v-tooltip>
                        </div>
                    </v-row>
                </v-card>
            </draggable>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import authType from '@/store/type/authType'
import routeType from '@/router/routeType'
import SelectApiClienType from '@/api/SelectApiClientType'
import TitleMixinGenerator from '@/components/mixins/page-title/TitleMixinGenerator'
import UserRouteTabs from '@/components/pages/users/RouteTabs'
import UserAddressesTable from '@/components/pages/userAddresses/Table'
import RestApiCollection from '@/api/RestApiCollection'
import MapperCollection from '@/services/mapper/MapperCollection'
import RestApiType from '@/api/RestApiType'
import MapperType from '@/services/mapper/MapperType'
import Overlay from '@/components/overlay/Overlay'
import isEqual from 'lodash/isEqual'
import routeInfoType from '@/store/type/routeInfoType'
import AddressesHeaders from '@/pages/users/mixins/AddressesHeaders'
import Draggable from 'vuedraggable'
import UserAddresses from '@/api/RestApi/UserAddresses'

const mapper = MapperCollection.get(MapperType.USERS_FORM)
const UserApi = RestApiCollection.get(RestApiType.USERS)

export default {
    components: {
        Draggable,
        UserAddressesTable,
        UserRouteTabs,
        Overlay,
    },
    mixins: [
        TitleMixinGenerator(SelectApiClienType.USER, 'userId'),
        AddressesHeaders,
    ],
    props: {
        userId: {
            type: [String, Number],
            default: null,
        },
    },
    data() {
        return {
            routeType,
            user: {},
            userForm: {},
            selectedCompanyAddresses: [],
            companyAddresses: [],
            total: 0,
            isCreate: false,
            busy: false,
            sortedAddresses: [],
            orderIsChanged: false,
            orderSaveLoading: false,
        }
    },

    computed: {
        ...mapGetters({
            isSuperAdmin: authType.getters.IS_SUPER_ADMIN,
        }),
        dragOptions() {
            return {
                animation: 200,
                ghostClass: 'ghost',
            }
        },
    },

    watch: {
        'userForm.companyAddresses': {
            handler(newVal) {
                this.selectedCompanyAddresses = []
                for (let val of newVal) {
                    const find = this.companyAddresses.find(
                        (el) => el['@id'] === val
                    )

                    this.selectedCompanyAddresses.push(find)
                }
            },
        },
    },

    mounted() {
        this.load()
    },

    methods: {
        async saveOrder() {
            this.orderSaveLoading = true
            const positions = this.sortedAddresses.map((x) => {
                return {
                    id: x.id,
                    position: x.position,
                }
            })
            await UserAddresses.savePositions(positions)
            this.orderSaveLoading = false
            Notification.success(this.$t('notifications.success'))
            this.orderIsChanged = false
        },
        orderChanged() {
            for (let index in this.sortedAddresses) {
                this.sortedAddresses[index].position = Number.parseInt(index)
            }
            this.orderIsChanged = true
        },
        async load() {
            this.busy = true

            const res = await this.$store.dispatch(
                routeInfoType.actions.GET_USER,
                this.userId
            )
            this.user = res

            if (res.company) {
                const data = await RestApiCollection.get(
                    RestApiType.COMPANY_ADDRESSES
                ).list({
                    page: 1,
                    itemsPerPage: 99,
                    'company[exact]': res.company['@id'],
                })

                this.companyAddresses = data['hydra:member']
                this.total = data['hydra:totalItems']
            }

            this.userForm = mapper.fromEntity(res)

            this.busy = false

            await this.loadSortList()
        },

        async loadSortList() {
            this.sortedAddresses = await UserAddresses.orderList(
                this.userForm.id
            )
        },

        async onInput(selections) {
            const ids = []
            for (let selection of selections) {
                ids.push(selection['@id'])
            }

            if (!isEqual(this.userForm.companyAddresses, ids)) {
                this.userForm.companyAddresses = ids

                this.busy = true
                await UserApi.update(
                    mapper.toEntity(this.userForm),
                    this.userId
                )
                this.busy = false
            }

            await this.loadSortList()
            this.orderChanged()
        },

        onCreate() {
            this.$refs.addressTable.$refs.iterator.updateItems(true, true)
            this.isCreate = false
        },
    },
}
</script>
